import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { hierarchyService } from '../../services/hierarchyService';
import { userService } from '../../services/userService';
import HierarchyEntityTable from './HierarchyEntityTable';
import ActionToast from '../../components/ui/ActionToast';

const Faculty = () => {
    const [faculties, setFaculties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [deans, setDeans] = useState([]);
    const [loadingDeans, setLoadingDeans] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [notice, setNotice] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        dean_id: '',
        description: ''
    });

    const fetchFaculties = async () => {
        try {
            setLoading(true);
            const data = await hierarchyService.getFaculties();
            setFaculties(data || []);
        } catch (error) {
            console.error('Failed to fetch faculties:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeans = async () => {
        try {
            setLoadingDeans(true);
            const data = await userService.getUsers({ role: 'dean_of_faculty' });
            const options = data.map(user => ({
                label: `${user.full_name} (${user.email})`,
                value: user.id
            }));
            setDeans(options);
        } catch (error) {
            console.error('Failed to fetch deans:', error);
        } finally {
            setLoadingDeans(false);
        }
    };

    useEffect(() => {
        fetchFaculties();
        fetchDeans();
    }, []);

    const clearForm = () => {
        setFormData({ name: '', code: '', dean_id: '', description: '' });
        setIsEditing(false);
        setEditId(null);
    };

    const handleEdit = (faculty) => {
        setFormData({
            name: faculty.name,
            code: faculty.code,
            dean_id: faculty.dean_id || '',
            description: faculty.description || ''
        });
        setEditId(faculty.id);
        setIsEditing(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this faculty? This action cannot be undone.')) {
            try {
                await hierarchyService.deleteFaculty(id);
                fetchFaculties();
            } catch (error) {
                console.error('Error deleting faculty:', error);
                alert('Failed to delete faculty. It may have related records.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await hierarchyService.updateFaculty(editId, formData);
                setNotice({ type: 'success', message: `${formData.name} was updated successfully.` });
            } else {
                await hierarchyService.createFaculty(formData);
                setNotice({ type: 'success', message: `${formData.name} was created successfully.` });
            }
            clearForm();
            setShowForm(false);
            fetchFaculties();
        } catch (error) {
            console.error('Error saving faculty:', error);
            setNotice({ type: 'error', message: error.message || 'The faculty could not be saved.' });
        }
    };

    return (
        <AdminLayout title="Faculty Management">
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Faculties</h1>
                        <p className="text-muted-foreground">Manage institution faculties and their Deans.</p>
                    </div>
                    <Button onClick={() => {
                        if (showForm && !isEditing) {
                            clearForm();
                            setShowForm(false);
                        } else {
                            clearForm();
                            setShowForm(true);
                        }
                    }}>
                        <Icon name={showForm && !isEditing ? "X" : "Plus"} className="mr-2 h-4 w-4" />
                        {showForm && !isEditing ? 'Cancel' : 'Add Faculty'}
                    </Button>
                </div>

                {showForm && (
                    <Card className="border-primary/20 bg-primary/5">
                        <CardHeader>
                            <CardTitle>{isEditing ? 'Edit Faculty' : 'Create New Faculty'}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input
                                        label="Faculty Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="e.g. Faculty of Science"
                                    />
                                    <Input
                                        label="Faculty Code"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        required
                                        placeholder="e.g. FOS"
                                    />
                                    <div className="space-y-1">
                                        <Select
                                            label="Dean of Faculty"
                                            options={deans}
                                            value={formData.dean_id}
                                            onChange={(val) => setFormData({ ...formData, dean_id: val })}
                                            loading={loadingDeans}
                                            placeholder="Select a Dean..."
                                            searchable
                                            required
                                        />
                                        <div className="text-xs text-muted-foreground text-right">
                                            Can't find the person?{' '}
                                            <NavLink to="/enhanced-user-management-system" className="text-primary hover:underline">
                                                Assign 'Dean' Role
                                            </NavLink>
                                        </div>
                                    </div>
                                    <Input
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Brief description..."
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                    <Button type="button" variant="outline" onClick={() => { clearForm(); setShowForm(false); }}>Cancel</Button>
                                    <Button type="submit">{isEditing ? 'Update Faculty' : 'Create Faculty'}</Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {loading ? (
                    <div className="text-center py-10">Loading faculties...</div>
                ) : (
                    <HierarchyEntityTable type="faculty" entities={faculties} onEdit={handleEdit} onDelete={handleDelete} emptyMessage='No faculties found. Click "Add Faculty" to create one.' />
                )}
            </div>
        </AdminLayout>
    );
};

export default Faculty;
